import axios from 'axios';
import { deviceHeaders } from './device';
import { recordOverrideOn } from './recordOverride';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refresh');
  localStorage.removeItem('user');
  localStorage.removeItem('screens');
  localStorage.removeItem('menu');
  localStorage.removeItem('admin_session');
  sessionStorage.removeItem('ashr_record_override');
};

/* "View as": the Admin's own session is parked here while a read-only preview token is in use. */
const ADMIN_SESSION = 'admin_session';
const SESSION_KEYS = ['token', 'refresh', 'user', 'screens'];

export const inPreview = () => !!localStorage.getItem(ADMIN_SESSION);

export const startPreview = ({ access, user }) => {
  if (!inPreview()) {
    localStorage.setItem(ADMIN_SESSION, JSON.stringify(Object.fromEntries(SESSION_KEYS.map((k) => [k, localStorage.getItem(k)]))));
  }
  localStorage.setItem('token', access);
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.removeItem('refresh');
  localStorage.removeItem('screens');
};

export const endPreview = () => {
  let saved = null;
  try {
    saved = JSON.parse(localStorage.getItem(ADMIN_SESSION) || 'null');
  } catch {
    saved = null;
  }
  localStorage.removeItem(ADMIN_SESSION);
  if (!saved) return false;
  SESSION_KEYS.forEach((k) => (saved[k] == null ? localStorage.removeItem(k) : localStorage.setItem(k, saved[k])));
  return true;
};

export const currentUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
};

// Paginated endpoints return {count, results}; older endpoints return a plain array.
export const asList = (data) => (Array.isArray(data) ? data : (data && Array.isArray(data.results) ? data.results : []));

// Same request body sent again within this window counts as a double submit (server replays the first answer).
const DUPLICATE_WINDOW_MS = 20000;

const hashText = (text) => {
  let h1 = 0x811c9dc5;
  let h2 = 0;
  for (let i = 0; i < text.length; i += 1) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
    h2 = (Math.imul(h2, 31) + c) >>> 0;
  }
  return `${h1.toString(36)}${h2.toString(36)}${text.length.toString(36)}`;
};

const idempotencyKey = (config) => {
  const data = config.data;
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    const parts = [];
    data.forEach((v, k) => parts.push(`${k}=${v && typeof v === 'object' ? `${v.name}:${v.size}:${v.lastModified}` : v}`));
    return parts.join('&');
  }
  return typeof data === 'string' ? data : JSON.stringify(data ?? {});
};

// Add token to requests; list requests without explicit paging get the full list (server caps it at 5000).
api.interceptors.request.use((config) => {
  Object.assign(config.headers, deviceHeaders());
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (recordOverrideOn()) {
    config.headers['X-Record-Override'] = '1';
  }
  const method = (config.method || 'get').toLowerCase();
  if (method !== 'get' && !(config.url || '').includes('/auth/') && !config.headers['X-Idempotency-Key']) {
    const bucket = Math.floor(Date.now() / DUPLICATE_WINDOW_MS);
    config.headers['X-Idempotency-Key'] = hashText(`${method} ${config.url} ${JSON.stringify(config.params || {})} ${idempotencyKey(config)} ${bucket}`);
  }
  if (method === 'get') {
    const params = config.params || {};
    const inUrl = /[?&](page|page_size)=/.test(config.url || '');
    if (!inUrl && params.page === undefined && params.page_size === undefined) {
      config.params = { ...params, page_size: 5000 };
    }
  }
  return config;
});

let refreshing = null;

const goToLogin = () => {
  if (inPreview() && endPreview()) {
    window.location.href = '/';
    return;
  }
  clearSession();
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// On 401 try the refresh token once, then send the user to the login page.
// On 403 with must_change_password, force the password screen (stale sessions after temp passwords).
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const data = error.response?.data || {};
    if (error.response?.status === 403 && data.must_change_password) {
      const u = currentUser() || {};
      if (!u.must_change_password) {
        localStorage.setItem('user', JSON.stringify({ ...u, must_change_password: true }));
        window.location.href = '/';
        return new Promise(() => {});
      }
    }
    const isAuthCall = (original.url || '').includes('/auth/login') || (original.url || '').includes('/auth/refresh');
    if (error.response?.status === 401 && !original._retried && !isAuthCall) {
      const refresh = localStorage.getItem('refresh');
      if (refresh) {
        try {
          refreshing = refreshing || axios.post(`${API_BASE_URL}/auth/refresh/`, { refresh });
          const r = await refreshing;
          localStorage.setItem('token', r.data.access);
          if (r.data.refresh) localStorage.setItem('refresh', r.data.refresh);
          original._retried = true;
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${r.data.access}` };
          return api(original);
        } catch {
          goToLogin();
        } finally {
          refreshing = null;
        }
      } else {
        goToLogin();
      }
    }
    return Promise.reject(error);
  }
);

export default api;
